import { Blocks, Shield, FileCheck, Key, Fingerprint, Lock } from "lucide-react";

const BlockchainSection = () => {
  const features = [
    {
      icon: Blocks,
      title: "Immutable Ledger",
      description: "Every property title, sale, and transfer is permanently recorded on our decentralized blockchain, creating an unchangeable history of ownership.",
    },
    {
      icon: Shield,
      title: "Proof of Ownership",
      description: "Cryptographically verify property ownership instantly. No more paper trails or uncertainty—blockchain provides undeniable proof.",
    },
    {
      icon: FileCheck,
      title: "Smart Contracts",
      description: "Automated, self-executing contracts ensure transactions complete only when all conditions are met, eliminating intermediary delays.",
    },
    {
      icon: Key,
      title: "Self-Guided Tours",
      description: "Access properties on your schedule with secure digital keys. Background and credit checks unlock tour capabilities.",
    },
    {
      icon: Fingerprint,
      title: "Identity Verification",
      description: "Comprehensive background checks and credit verification ensure only qualified individuals access properties.",
    },
    {
      icon: Lock,
      title: "Secure Transactions",
      description: "End-to-end encryption and blockchain security protect your financial information throughout every transaction.",
    },
  ];

  return (
    <section id="blockchain" className="py-24 lg:py-32 bg-slate-900" data-testid="blockchain-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C04000] mb-4 block">
            Blockchain Technology
          </span>
          <h2 
            className="text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="blockchain-heading"
          >
            The Future of Real Estate
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            We've built our platform on blockchain technology to bring transparency, security, and efficiency to every real estate transaction.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="blockchain-card rounded-lg p-6 lg:p-8"
              data-testid={`blockchain-feature-${index}`}
            >
              <div className="w-12 h-12 bg-[#C04000]/20 rounded-lg flex items-center justify-center mb-5">
                <feature.icon className="w-6 h-6 text-[#C04000]" />
              </div>
              <h3 
                className="text-xl font-semibold text-white mb-3"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {feature.title}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Abstract Image */}
        <div className="mt-16 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900 z-10 pointer-events-none"></div>
          <img
            src="https://images.unsplash.com/photo-1644190022446-04b99df7259a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJsb2NrY2hhaW4lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3NTI3NDQ5NXww&ixlib=rb-4.1.0&q=85"
            alt="Blockchain network visualization"
            className="w-full h-48 lg:h-64 object-cover rounded-lg opacity-30"
            data-testid="blockchain-image"
          />
        </div>
      </div>
    </section>
  );
};

export default BlockchainSection;
